import React, { useEffect, useMemo, useState } from 'react';
import { Story, useApp } from '../../context/AppContext';
import { useBedtimeContext } from '../../context/BedtimeContext';
import BedtimePairChip from '../../components/BedtimeContext/BedtimePairChip';
import StoryWorldTile from '../../components/StoryWorlds/StoryWorldTile';
import { groupStoryWorlds } from '../../components/StoryWorlds/storyWorldModel';
import { EmptyState, Shelf, StateMessage } from '../../components/ui';
import { formatDurationLabel } from '../../utils/duration';
import { useApprovedRenders } from './useApprovedRenders';
import './Home.css';

const DISCOVERY_LIMIT = 12;

function greetingFor(date: Date): string {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Home: greeting, the pair chip, the approved shelf for tonight's listener
 * and voice, then a discovery shelf from the catalog. Prerequisites render
 * one honest empty state with one action; catalog art is never called ready.
 */
const Home: React.FC = () => {
  const { state, fetchStories } = useApp();
  const bedtime = useBedtimeContext();
  const [currentTime, setCurrentTime] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    void fetchStories();
  }, [fetchStories]);

  const pair = bedtime.pair;
  const approved = useApprovedRenders(pair, bedtime.status === 'ready');
  const listener = bedtime.listeners.find((item) => item.listener_id === pair?.listenerId);
  const voice = bedtime.voices.find((item) => item.voice_id === pair?.voiceId);
  const listenerName = listener?.display_name || 'this listener';
  const voiceName = voice?.voice_name || 'Family voice';

  const approvedIds = useMemo(() => new Set(approved.renders.map((render) => render.story_id)), [approved.renders]);
  const approvedWorlds = useMemo(() => {
    const byStory = new Map(approved.renders.map((render) => [render.story_id, render]));
    const stories: Story[] = approved.renders.map((render) => state.stories.find((story) => story.story_id === render.story_id) || {
      story_id: render.story_id || render.render_id,
      title: 'Saved bedtime story',
      description: 'An approved story saved for your bedtime.',
      age_range: '',
      tags: [],
      estimated_duration_minutes: render.estimated_duration_minutes,
    });
    return groupStoryWorlds(stories).map((world) => {
      const first = world.chapters[0];
      const render = byStory.get(first.story_id);
      return {
        world,
        to: `/my-stories?story=${encodeURIComponent(first.story_id)}`,
        duration: formatDurationLabel(first.estimated_duration_minutes ?? render?.estimated_duration_minutes, render?.output_seconds),
        metadata: world.serialized
          ? [`${world.chapters.length} chapters available`, voiceName]
          : [voiceName],
      };
    });
  }, [approved.renders, state.stories, voiceName]);

  const discoveryWorlds = useMemo(() => (
    groupStoryWorlds(state.stories.filter((story) => !approvedIds.has(story.story_id)))
      .slice(0, DISCOVERY_LIMIT)
      .map((world) => {
        const first = world.chapters[0];
        return {
          world,
          to: `/discover?story=${encodeURIComponent(first.story_id)}`,
          duration: formatDurationLabel(first.estimated_duration_minutes),
        };
      })
  ), [approvedIds, state.stories]);

  const storiesPending = state.isLoading && state.stories.length === 0;
  const storiesFailed = Boolean(state.error && state.stories.length === 0);

  const renderPrerequisite = () => {
    switch (bedtime.status) {
      case 'needs_selection':
        return <EmptyState headingLevel={2} title="Choose bedtime" message="Pick who is listening and the Family voice they will hear." action={{ label: 'Choose bedtime', onClick: () => bedtime.openPairDialog() }} id="home-prerequisite" />;
      case 'no_listener':
        return <EmptyState headingLevel={2} title="Add a listener first" message="Luma uses a listener to keep stories and goodnights personal." action={{ label: 'Add a listener', to: '/account' }} id="home-prerequisite" />;
      case 'no_ready_voice':
        return <EmptyState headingLevel={2} title="Add a family voice" message="Add a family voice before making a story." action={{ label: 'Add a family voice', to: '/voice' }} id="home-prerequisite" />;
      case 'missing_bookends':
        return pair ? (
          <EmptyState headingLevel={2} title={`Add ${listenerName}’s hello and goodnight`} message="Save the three personal messages before making or playing stories for this listener." action={{ label: 'Record personal messages', to: `/voice/${pair.voiceId}/personalize/${pair.listenerId}` }} id="home-prerequisite" />
        ) : null;
      default:
        return null;
    }
  };

  const renderApprovedShelf = () => {
    if (bedtime.status !== 'ready') return null;
    if (approved.state === 'loading' || approved.state === 'idle') {
      return <StateMessage kind="loading" title="Loading your bedtime shelf…" shape="shelf" testId="home-approved-loading" />;
    }
    if (approved.state === 'error') {
      return <StateMessage kind="error" error={approved.error} retry={{ onClick: approved.reload }} testId="home-approved-error" />;
    }
    if (approved.state === 'empty') {
      return <EmptyState headingLevel={2} title="Your bedtime shelf starts here" message="Listen and approve a story in Tonight to add it here." action={{ label: 'Open Tonight', to: '/renders' }} id="home-approved-empty" />;
    }
    return (
      <Shelf
        id="home-ready"
        title="Ready for bedtime"
        items={approvedWorlds}
        getKey={(item) => item.world.id}
        destination={{ to: '/my-stories' }}
        renderItem={(item, index) => (
          <StoryWorldTile world={item.world} to={item.to} duration={item.duration} metadata={item.metadata} loading={index < 4 ? 'eager' : 'lazy'} />
        )}
      />
    );
  };

  const renderDiscoveryShelf = () => {
    if (storiesPending) return <StateMessage kind="loading" title="Loading stories…" shape="shelf" testId="home-discovery-loading" />;
    if (storiesFailed) return <StateMessage kind="error" title="We couldn’t load stories" message="Try again in a moment." retry={{ onClick: () => void fetchStories() }} testId="home-discovery-error" />;
    if (discoveryWorlds.length === 0) return null;
    return (
      <Shelf
        id="home-discover"
        title="Stories to discover"
        items={discoveryWorlds}
        getKey={(item) => item.world.id}
        destination={{ to: '/discover' }}
        renderItem={(item, index) => (
          <StoryWorldTile world={item.world} to={item.to} duration={item.duration} loading={index < 4 ? 'eager' : 'lazy'} />
        )}
      />
    );
  };

  return (
    <div className="home">
      <h1 className="home__greeting">{greetingFor(currentTime)}</h1>
      <BedtimePairChip />
      {renderPrerequisite()}
      {renderApprovedShelf()}
      {renderDiscoveryShelf()}
    </div>
  );
};

export default Home;
